"use client";

import { useState, useEffect, useRef } from "react";
import { TaxCounter } from "./TaxCounter";

// Reusable component for hover-reveal text lines
interface RevealLineProps {
  normalText: string;
  revealText: string;
  isFirst?: boolean;
  isLast?: boolean;
  index: number;
  onHover: (index: number) => void;
  onLeave: () => void;
  linePadding: string; // Geräte-spezifisches Padding
  fontSize: string; // Geräte-spezifische Schriftgröße
}

function RevealLine({
  normalText,
  revealText,
  isFirst,
  index,
  onHover,
  onLeave,
  linePadding,
  fontSize,
}: RevealLineProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const hasTriggeredRef = useRef(false);

  // IntersectionObserver statt Scroll-Listener (1 Observer statt RAF pro Element)
  useEffect(() => {
    if (hasTriggeredRef.current || !lineRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          observer.disconnect();
          // Verzögerung basierend auf Index für gestaffelten Effekt
          setTimeout(() => {
            setIsVisible(true);
          }, index * 50);
        }
      },
      { threshold: 0, rootMargin: "-15% 0px 0px 0px" } // Trigger bei ~85% im Viewport
    );

    observer.observe(lineRef.current);

    return () => observer.disconnect();
  }, [index]);

  // Styles: Animation auf allen Geräten
  const containerStyle: React.CSSProperties = {
    position: "relative", // WICHTIG: Für absolute Positionierung des Curtain-Layers
    padding: linePadding,
    borderTop: isFirst ? "1px solid rgba(255,255,255,0.1)" : "none",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    // Animation: Einblenden + Rotation (schnell)
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "rotateX(0deg)" : "rotateX(-90deg)",
    transformOrigin: "top center",
    transition: "opacity 0.4s ease-out, transform 0.4s ease-out",
    visibility: "visible",
    display: "block",
  };

  return (
    <div ref={lineRef} style={containerStyle}>
      {/* Base text */}
      <h3
        className="uppercase m-0 relative"
        style={{
          fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
          fontWeight: 700,
          fontSize: fontSize,
          letterSpacing: "-0.03em",
          lineHeight: "100%",
          color: "#b7ab98",
        }}
        onMouseEnter={() => {
          setIsHovered(true);
          onHover(index);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          onLeave();
        }}
      >
        {normalText}
      </h3>

      {/* Curtain layer - clips from center to reveal alternate text */}
      <div
        className="absolute inset-0 flex items-center pointer-events-none"
        style={{
          backgroundColor: "#E85D3B",
          clipPath: isHovered ? "inset(0% 0)" : "inset(50% 0)",
          transition: "all 0.5s ease-out",
        }}
      >
        <h3
          className="uppercase m-0"
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 700,
            fontSize: fontSize,
            letterSpacing: "-0.03em",
            lineHeight: "100%",
            color: "#0d0d0d",
          }}
        >
          {revealText}
        </h3>
      </div>
    </div>
  );
}

// Data for the reveal lines - logical order based on income flow:
// 1. Earning phase (income taxes + social contributions)
// 2. Spending phase (consumption taxes)
// 3. Saving/investing phase
// 4. End of life (inheritance)
const revealLines = [
  // --- EARNING PHASE ---
  { normal: "Du arbeitest", reveal: "Lohnsteuer" },
  { normal: "Du lebst", reveal: "Sozialabgaben" },
  { normal: "Du glaubst", reveal: "Kirchensteuer" },
  // --- SPENDING PHASE (daily life) ---
  { normal: "Du kaufst", reveal: "Mehrwertsteuer" },
  { normal: "Du kochst", reveal: "Energiesteuer" },
  { normal: "Du genießt", reveal: "Kaffeesteuer" },
  { normal: "Du fährst", reveal: "Kraftfahrzeugsteuer" },
  { normal: "Du erkundest", reveal: "Luftverkehrsteuer" },
  { normal: "Du wohnst", reveal: "Grundsteuer" },
  { normal: "Du hältst", reveal: "Hundesteuer" },
  // --- SAVING/INVESTING PHASE ---
  { normal: "Du sparst", reveal: "EZB-Steuer" },
  { normal: "Du investierst", reveal: "Kapitalertragsteuer" },
  // --- END OF LIFE ---
  { normal: "Du verlierst", reveal: "Erbschaftssteuer" },
];

export function ProblemSection() {
  const [activeLineIndex, setActiveLineIndex] = useState(-1);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Device Detection für geräte-spezifische Styles
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Geräte-spezifische Werte berechnen
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;
  const isLargeDesktop = windowWidth >= 2400; // Große Monitore (2560x1440 etc.)

  // Container Padding und Schriftgröße: Desktop kleiner, damit alles in den Viewport passt
  let containerPadding: string;
  let labelMarginBottom: string;
  let linePadding: string;
  let fontSize: string;

  if (isMobile) {
    containerPadding = "100px 16px";
    labelMarginBottom = "2.5rem";
    linePadding = "12px 0"; // py-3 equivalent
    fontSize = "clamp(1.5rem, 5vw, 2.5rem)";
  } else if (isTablet) {
    containerPadding = "120px 32px";
    labelMarginBottom = "4rem";
    linePadding = "10px 0";
    fontSize = "clamp(1.5rem, 4vw, 2.25rem)";
  } else if (isLargeDesktop) {
    // Große Monitore: Mehr Platz, größere Schrift möglich
    containerPadding = "96px 32px";
    labelMarginBottom = "6rem";
    linePadding = "10px 0";
    fontSize = "clamp(2rem, 2vw, 3rem)"; // Größere Schrift für große Bildschirme
  } else {
    // Desktop: Kompaktere Werte für Viewport-Fit
    containerPadding = "64px 32px";
    labelMarginBottom = "6rem"; // Label-Abstand bleibt
    linePadding = "6px 0"; // Reduziertes Padding
    fontSize = "clamp(1.5rem, 2.5vw, 2.25rem)"; // Max etwas erhöht
  }

  const handleLineHover = (index: number) => {
    setActiveLineIndex(index);
  };

  const handleLineLeave = () => {
    // Keep the last hovered index for a moment, then reset
    // This prevents flickering when moving between lines
  };

  // Auf Tablets: kein overflow-hidden, damit alle Zeilen sichtbar sind
  const sectionOverflow = isTablet || isMobile ? "visible" : "hidden";

  return (
    <section
      className="relative w-full"
      style={{
        minHeight: isTablet || isMobile ? "auto" : "100vh",
        overflow: sectionOverflow,
        backgroundColor: "#0d0d0d",
      }}
    >
      {/* Content Container */}
      <div className="relative w-full h-full flex flex-col">
        {/* Inner container for navigation alignment */}
        <div
          className="w-full max-w-7xl mx-auto"
          style={{ padding: containerPadding }}
        >
          {/* Two-Column Layout */}
          <div
            className="grid grid-cols-1 lg:grid-cols-2 items-start"
            style={{ gap: isLargeDesktop || (!isMobile && !isTablet) ? "4rem" : "2rem" }}
          >
            {/* Left Column - Label + Reveal Lines with flip animation */}
            <div className="flex flex-col" style={{ perspective: "1000px" }}>
              {/* "heutige Realität" label - inside left column for alignment */}
              <p
                className="font-bold text-sm uppercase tracking-[0.2em] text-[#b7ab98] select-none"
                style={{ marginBottom: labelMarginBottom, marginTop: 0 }}
              >
                heutige Realität
              </p>
              {revealLines.map((line, index) => (
                <RevealLine
                  key={index}
                  index={index}
                  normalText={line.normal}
                  revealText={line.reveal}
                  isFirst={index === 0}
                  isLast={index === revealLines.length - 1}
                  onHover={handleLineHover}
                  onLeave={handleLineLeave}
                  linePadding={linePadding}
                  fontSize={fontSize}
                />
              ))}
            </div>

            {/* Right Column - Tax Counter */}
            <div
              className="flex items-start justify-center"
              style={{
                justifyContent: !isMobile && !isTablet ? "flex-end" : "center",
                position: !isMobile && !isTablet ? "sticky" : undefined,
                top: !isMobile && !isTablet ? 64 : undefined,
                alignSelf: "flex-start",
              }}
            >
              <TaxCounter activeIndex={activeLineIndex} className="w-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
